/** docs/UX.md 15 · `/m/qc` 质检 (+ docs/ROADMAP.md A5 黄金题入口) */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { RecordingQcPayload, Task } from "@xinghai/shared";
import {
  AudioPlayer,
  Button,
  EmptyState,
  Field,
  Icon,
  Sheet,
  Textarea,
  useConfirmSafe,
  useToast,
} from "@xinghai/ui";
import type { AudioPlayerHandle } from "@xinghai/ui";
import { GoldenSheet } from "../annotator";
import { materialsApi, qcApi, recordingAudioUrl } from "../../lib/api";
import { qk } from "../../lib/queries";
import {
  ErrorNotice,
  LoadingBlock,
  PageHeader,
  QueryBoundary,
  StatusTag,
  formatDuration,
  formatPercent,
} from "./common";
import { DOMAIN_LABEL, TASK_LABEL } from "./labels";
import type { QcRecordingItem } from "./types";
import { AiScoreCard } from "../ai/AiScoreCard";
import { aiPollInterval } from "../ai/poll";

/** docs/UX.md §3.2 — 原因 4 选 1 + 备注. */
export const REJECT_REASONS = [
  "背景噪声太大",
  "有削波失真，听不清",
  "静音太多 / 没录到内容",
  "读错字或漏读",
];

/** §9 母带规范 — the SNR a take has to clear to pass without listening. */
export const SNR_GATE_DB = 45;

/** One page of the QC queue. The caption uses `total`, not this page's length. */
export const QC_PAGE_SIZE = 200;

/**
 * Pending count printed on the QC page. Prefer the server's uncapped `total`
 * so a 3347-item queue is not labelled as 200.
 */
export function qcPendingTotal(total: number | undefined, visible: number): number {
  return typeof total === "number" ? total : visible;
}

export function snrOf(r: QcRecordingItem): number | null {
  const v = r.client_meta?.snr_db;
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

export function goodSnr(rows: readonly QcRecordingItem[], gate = SNR_GATE_DB): QcRecordingItem[] {
  return rows.filter((r) => {
    const snr = snrOf(r);
    return snr !== null && snr >= gate && !r.client_meta?.clipping;
  });
}

export function QcPage() {
  const [rejecting, setRejecting] = useState<QcRecordingItem | null>(null);
  const [golden, setGolden] = useState<QcRecordingItem | null>(null);
  const [mode, setMode] = useState<"focus" | "list">("focus");
  const [handled, setHandled] = useState<string[]>([]);
  const toast = useToast();
  const confirm = useConfirmSafe();
  const qc = useQueryClient();

  // C3 覆盖热图的钻取：`/m/qc?speaker_id=…&task=…&all_states=1`
  // 只是**收窄**这个页面的队列；班级范围由服务端按授权矩阵强制，前端改不动。
  const [params, setParams] = useSearchParams();
  const drill = useMemo(() => {
    const speakerId = params.get("speaker_id") ?? "";
    const task = params.get("task") ?? "";
    const allStates = params.get("all_states") === "1";
    return {
      active: !!speakerId || !!task,
      ...(speakerId ? { speaker_id: speakerId } : {}),
      ...(task ? { task } : {}),
      ...(allStates ? { all_states: true } : {}),
    };
  }, [params]);

  const list = useInfiniteQuery({
    queryKey: qk.qcQueue(`uploaded:${params.toString()}`),
    initialPageParam: 0,
    queryFn: ({ signal, pageParam }) =>
      qcApi.list("uploaded", signal, {
        ...drill,
        limit: QC_PAGE_SIZE,
        offset: pageParam,
      }),
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce((n, page) => n + page.recordings.length, 0);
      const total = lastPage.total ?? loaded;
      if (lastPage.recordings.length === 0 || loaded >= total) return undefined;
      return loaded;
    },
    refetchInterval: (query) =>
      aiPollInterval(query.state.data?.pages.flatMap((page) => page.recordings) ?? []),
  });

  const decide = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: RecordingQcPayload }) =>
      qcApi.decide(id, payload),
    onSuccess: (_r, vars) => {
      toast.success(
        vars.payload.decision === "qc_ok" ? "已通过，进入标注池" : "已驳回，学生可以重录",
      );
      // auto-advance: drop it from the queue locally, then re-sync
      setHandled((prev) => (prev.includes(vars.id) ? prev : [...prev, vars.id]));
      void qc.invalidateQueries({ queryKey: ["qc"] });
      void qc.invalidateQueries({ queryKey: qk.qualitySummary });
      setRejecting(null);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const bulk = useMutation({
    mutationFn: (ids: string[]) =>
      qcApi.bulk({ decision: "qc_ok", recording_ids: ids, min_snr_db: SNR_GATE_DB }),
    onSuccess: (r, ids) => {
      toast.success(`已通过 ${r.updated ?? ids.length} 条`);
      setHandled((prev) => [...new Set([...prev, ...(r.recording_ids ?? ids)])]);
      void qc.invalidateQueries({ queryKey: ["qc"] });
      void qc.invalidateQueries({ queryKey: qk.qualitySummary });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const all = useMemo(
    () => list.data?.pages.flatMap((page) => page.recordings) ?? [],
    [list.data],
  );
  const rows = useMemo(() => all.filter((r) => !handled.includes(r.id)), [all, handled]);
  const pendingTotal = qcPendingTotal(list.data?.pages[0]?.total, rows.length);
  useEffect(() => {
    if (rows.length === 0 && list.hasNextPage && !list.isFetchingNextPage) {
      void list.fetchNextPage();
    }
  }, [rows.length, list.hasNextPage, list.isFetchingNextPage, list.fetchNextPage]);
  const current = rows[0] ?? null;
  const eligible = useMemo(() => goodSnr(rows), [rows]);

  const pass = useCallback(
    (r: QcRecordingItem) =>
      decide.mutate({
        id: r.id,
        payload: {
          decision: "qc_ok",
          clipping: r.client_meta?.clipping ?? false,
          silence_ratio: r.client_meta?.silence_ratio ?? null,
          too_long: false,
          note: null,
        },
      }),
    [decide],
  );

  // docs/UX.md §3.2 — Enter 通过, X 驳回. Never while a sheet or a field has focus.
  const overlayOpen = rejecting !== null || golden !== null;
  useEffect(() => {
    if (mode !== "focus" || !current || overlayOpen) return;
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))) return;
      if (e.key === "Enter") {
        e.preventDefault();
        pass(current);
      } else if (e.key === "x" || e.key === "X") {
        e.preventDefault();
        setRejecting(current);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mode, current, overlayOpen, pass]);

  const askBulk = async () => {
    const ok = await confirm({
      title: `通过这 ${eligible.length} 条？`,
      message: `这些录音信噪比都达到了 ${SNR_GATE_DB} dB 且没有削波。通过之后它们直接进入标注池。`,
      confirmLabel: "全部通过",
    });
    if (ok) bulk.mutate(eligible.map((r) => r.id));
  };

  return (
    <div className="xz-adm">
      <PageHeader
        title="质检"
        description="一条一屏：听一遍，通过或驳回，自动跳到下一条。驳回必须写原因，学生会看到并重录。"
        actions={
          <>
            <Button
              variant="secondary"
              disabled={eligible.length === 0}
              loading={bulk.isPending}
              leadingIcon={<Icon id="ui-check" size={16} />}
              onClick={() => void askBulk()}
            >
              通过全部 SNR≥{SNR_GATE_DB} dB（{eligible.length}）
            </Button>
            <Button
              variant="ghost"
              leadingIcon={<Icon id={mode === "focus" ? "ui-list" : "ui-eye"} size={16} />}
              onClick={() => setMode(mode === "focus" ? "list" : "focus")}
            >
              {mode === "focus" ? "看全部列表" : "回到一条一屏"}
            </Button>
            <Button
              variant="ghost"
              leadingIcon={<Icon id="ui-refresh" size={16} />}
              onClick={() => {
                setHandled([]);
                void list.refetch();
              }}
            >
              刷新队列
            </Button>
          </>
        }
      />

      {drill.active && (
        <p className="gz-notice xz-adm__notice" role="status">
          <Icon id="ui-filter" size={20} />
          <span>
            只看
            {drill.speaker_id ? ` ${drill.speaker_id}` : ""}
            {drill.task ? ` 的「${TASK_LABEL[drill.task as Task] ?? drill.task}」` : "的"}
            录音（来自班级覆盖热图）。
          </span>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setHandled([]);
              setParams({}, { replace: true });
            }}
          >
            清除筛选
          </Button>
        </p>
      )}

      <QueryBoundary
        isPending={list.isPending}
        isError={list.isError}
        error={list.error}
        onRetry={() => void list.refetch()}
      >
        {rows.length === 0 && (list.hasNextPage || list.isFetchingNextPage) ? (
          <LoadingBlock label="正在加载更多待质检录音…" />
        ) : rows.length === 0 ? (
          <EmptyState
            title={drill.active ? "这一格没有待质检的录音" : "没有待质检的录音"}
            description={
              drill.active
                ? "这个学生在这个环节没有等待质检的条目。"
                : "学生上传新录音后会出现在这里。"
            }
          />
        ) : mode === "focus" && current ? (
          <>
            <p className="gz-caption">还剩 {pendingTotal} 条</p>
            <QcFocus
              r={current}
              busy={decide.isPending}
              onPass={() => pass(current)}
              onReject={() => setRejecting(current)}
              onGolden={() => setGolden(current)}
            />
          </>
        ) : (
          <>
            <p className="gz-caption">共 {pendingTotal} 条待质检</p>
            <div className="xz-stack">
              {rows.map((r) => (
                <QcCard
                  key={r.id}
                  r={r}
                  busy={decide.isPending}
                  onPass={() => pass(r)}
                  onReject={() => setRejecting(r)}
                  onGolden={() => setGolden(r)}
                />
              ))}
              {list.hasNextPage && (
                <Button
                  variant="secondary"
                  loading={list.isFetchingNextPage}
                  onClick={() => void list.fetchNextPage()}
                >
                  加载更多
                </Button>
              )}
            </div>
          </>
        )}
      </QueryBoundary>

      {golden && (
        <GoldenForRecording
          recording={golden}
          onClose={() => setGolden(null)}
          onSaved={() => void qc.invalidateQueries({ queryKey: ["qc"] })}
        />
      )}

      {rejecting && (
        <RejectSheet
          recording={rejecting}
          busy={decide.isPending}
          onClose={() => setRejecting(null)}
          onSubmit={(note) =>
            decide.mutate({
              id: rejecting.id,
              payload: {
                decision: "qc_reject",
                clipping: rejecting.client_meta?.clipping ?? false,
                silence_ratio: rejecting.client_meta?.silence_ratio ?? null,
                too_long: false,
                note,
              },
            })
          }
        />
      )}
    </div>
  );
}

/**
 * docs/ROADMAP.md A5 — 黄金题入口. The sheet itself lives in `features/annotator`
 * (it shares the mark vocabulary with the workbench); here we only resolve the
 * recording's material so it can render one row per char.
 */
function GoldenForRecording({
  recording,
  onClose,
  onSaved,
}: {
  recording: QcRecordingItem;
  onClose: () => void;
  onSaved: () => void;
}) {
  const q = useQuery({
    queryKey: qk.material(recording.material_id),
    queryFn: () => materialsApi.get(recording.material_id),
  });

  const chars = useMemo(
    () =>
      (q.data?.material.chars ?? []).map((c, index) => ({
        index,
        char: c.char,
        pinyin_locked: c.pinyin_locked,
        initial: c.initial,
        final: c.final,
        tone: c.tone,
        zhe: c.zhe,
        confusion_risk: c.confusion_risk,
      })),
    [q.data],
  );

  if (q.isPending || q.isError || chars.length === 0) {
    return (
      <Sheet
        open
        onClose={onClose}
        title="设为黄金题"
        footer={
          <Button variant="secondary" onClick={onClose}>
            关闭
          </Button>
        }
      >
        {q.isPending ? (
          <LoadingBlock rows={2} label="正在加载素材" />
        ) : q.isError ? (
          <ErrorNotice error={q.error} onRetry={() => void q.refetch()} title="素材没能加载" />
        ) : (
          <p className="gz-muted">这条素材没有逐字信息，无法录入标准答案。</p>
        )}
      </Sheet>
    );
  }

  return (
    <GoldenSheet
      open
      onClose={onClose}
      recordingId={recording.id}
      chars={chars}
      domain={recording.domain}
      onSaved={onSaved}
    />
  );
}

/** Shared hint chips: 削波 / 静音比 / SNR. */
function QcHints({ r }: { r: QcRecordingItem }) {
  const meta = r.client_meta ?? {};
  const snr = snrOf(r);
  return (
    <div className="xz-qc__hints">
      {snr !== null && (
        <StatusTag icon="ui-chart-line" tone={snr >= SNR_GATE_DB ? "success" : "warning"}>
          信噪比 {snr.toFixed(0)} dB{snr >= SNR_GATE_DB ? "（达标）" : "（偏低）"}
        </StatusTag>
      )}
      {meta.clipping ? (
        <StatusTag icon="ui-warning" tone="danger">
          削波 {meta.clip_count ? `${meta.clip_count} 处` : ""}
        </StatusTag>
      ) : (
        <StatusTag icon="ui-check">无削波</StatusTag>
      )}
      {meta.silence_ratio !== undefined && (
        <StatusTag icon="ui-percent" tone={meta.silence_ratio > 0.6 ? "warning" : "neutral"}>
          静音比 {formatPercent(meta.silence_ratio, 0)}
        </StatusTag>
      )}
      {meta.forced && (
        <StatusTag icon="ui-warning" tone="warning">
          学生忽略提示强行提交
        </StatusTag>
      )}
    </div>
  );
}

/**
 * docs/UX.md §3.2 一条一屏: auto-play, material, SNR/clipping, two big
 * buttons with keyboard equivalents, auto-advance handled by the parent.
 */
function QcFocus({
  r,
  busy,
  onPass,
  onReject,
  onGolden,
}: {
  r: QcRecordingItem;
  busy: boolean;
  onPass: () => void;
  onReject: () => void;
  onGolden: () => void;
}) {
  const player = useRef<AudioPlayerHandle>(null);
  const text = r.material?.text ?? r.material_text ?? null;

  // auto-play each new item; a browser may refuse before a user gesture.
  useEffect(() => {
    try {
      player.current?.play();
    } catch {
      /* autoplay blocked — the play button is right there */
    }
  }, [r.id]);

  return (
    <article className="xz-qc1" aria-label={`质检录音 ${r.id}`}>
      <header className="xz-qc__head">
        <span className="xz-qc__id xz-adm__mono">{r.id}</span>
        <StatusTag icon="ui-user">{r.speaker_id}</StatusTag>
        <StatusTag icon="ui-list-ordered">{TASK_LABEL[r.task]}</StatusTag>
        <StatusTag icon={r.domain === "singing" ? "ui-speaking" : "ui-reading"}>
          {DOMAIN_LABEL[r.domain]}
        </StatusTag>
        <StatusTag icon="ui-clock">{formatDuration(r.duration_ms)}</StatusTag>
      </header>

      <p className="xz-qc1__text">
        {text ?? <span className="gz-muted">素材 {r.material_id}</span>}
      </p>

      <AiScoreCard ai={r.ai} text={text} />

      <AudioPlayer
        ref={player}
        src={r.audio_url ?? recordingAudioUrl(r.id)}
        label={`播放录音 ${r.id}`}
      />

      <QcHints r={r} />

      <div className="xz-qc1__big">
        <Button
          size="lg"
          leadingIcon={<Icon id="ui-check" size={20} />}
          disabled={busy}
          onClick={onPass}
        >
          通过
        </Button>
        <Button
          size="lg"
          variant="danger"
          leadingIcon={<Icon id="ui-ban" size={20} />}
          disabled={busy}
          onClick={onReject}
        >
          驳回
        </Button>
      </div>

      <p className="xz-qc1__keys">
        <span>
          <kbd className="xz-kbd">Enter</kbd> 通过
        </span>
        <span>
          <kbd className="xz-kbd">X</kbd> 驳回
        </span>
        <span>
          <kbd className="xz-kbd">空格</kbd> 播放 / 暂停（先点一下播放器）
        </span>
      </p>

      <div className="xz-qc__actions">
        <Button
          variant="ghost"
          leadingIcon={<Icon id="ui-star" size={16} />}
          disabled={busy}
          onClick={onGolden}
        >
          设为黄金题
        </Button>
      </div>
    </article>
  );
}

function QcCard({
  r,
  busy,
  onPass,
  onReject,
  onGolden,
}: {
  r: QcRecordingItem;
  busy: boolean;
  onPass: () => void;
  onReject: () => void;
  onGolden: () => void;
}) {
  const text = r.material?.text ?? r.material_text ?? null;
  return (
    <article className="xz-qc">
      <header className="xz-qc__head">
        <span className="xz-qc__id xz-adm__mono">{r.id}</span>
        <StatusTag icon="ui-user">{r.speaker_id}</StatusTag>
        <StatusTag icon="ui-list-ordered">{TASK_LABEL[r.task]}</StatusTag>
        <StatusTag icon={r.domain === "singing" ? "ui-speaking" : "ui-reading"}>
          {DOMAIN_LABEL[r.domain]}
        </StatusTag>
        <StatusTag icon="ui-clock">{formatDuration(r.duration_ms)}</StatusTag>
      </header>

      <p className="xz-qc__text">
        {text ?? <span className="gz-muted">素材 {r.material_id}</span>}
      </p>

      <AiScoreCard ai={r.ai} text={text} />

      <AudioPlayer src={r.audio_url ?? recordingAudioUrl(r.id)} label={`播放录音 ${r.id}`} />

      <QcHints r={r} />

      <div className="xz-qc__actions">
        <Button leadingIcon={<Icon id="ui-check" size={16} />} disabled={busy} onClick={onPass}>
          通过
        </Button>
        <Button
          variant="danger"
          leadingIcon={<Icon id="ui-ban" size={16} />}
          disabled={busy}
          onClick={onReject}
        >
          驳回
        </Button>
        <Button
          variant="ghost"
          leadingIcon={<Icon id="ui-star" size={16} />}
          disabled={busy}
          onClick={onGolden}
        >
          设为黄金题
        </Button>
      </div>
    </article>
  );
}

/** Reason + optional note, joined into the single `note` the API takes. */
export function rejectNote(reason: string, extra: string): string {
  const tail = extra.trim();
  return tail ? `${reason}：${tail}` : reason;
}

function RejectSheet({
  recording,
  busy,
  onClose,
  onSubmit,
}: {
  recording: QcRecordingItem;
  busy: boolean;
  onClose: () => void;
  onSubmit: (note: string) => void;
}) {
  const [reason, setReason] = useState("");
  const [extra, setExtra] = useState("");
  const invalid = reason === "";

  return (
    <Sheet
      open
      onClose={onClose}
      title={`驳回录音 ${recording.id}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            取消
          </Button>
          <Button
            variant="danger"
            loading={busy}
            disabled={invalid}
            onClick={() => {
              if (!invalid) onSubmit(rejectNote(reason, extra));
            }}
          >
            确认驳回
          </Button>
        </>
      }
    >
      <div className="xz-form">
        <p className="gz-muted">原因会原样展示给学生，让他知道该怎么重录。</p>

        <fieldset className="xz-form__choices">
          <legend>驳回原因（必选）</legend>
          {REJECT_REASONS.map((r) => (
            <label key={r} className="xz-form__choice">
              <input
                type="radio"
                name="qc-reject-reason"
                value={r}
                checked={reason === r}
                onChange={() => setReason(r)}
              />
              <span>
                <span>{r}</span>
              </span>
            </label>
          ))}
        </fieldset>

        <Field label="补充说明（可选）" hint="写清楚具体怎么改，比如「换个安静的房间再录」。">
          <Textarea
            rows={3}
            value={extra}
            maxLength={400}
            placeholder="例如：后面有人说话，换个安静的地方重录。"
            onChange={(e) => setExtra(e.target.value)}
          />
        </Field>

        {invalid && (
          <p className="gz-caption" role="status">
            选一个原因就能提交。
          </p>
        )}
      </div>
    </Sheet>
  );
}
